import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import styled from "styled-components";
import {WATERSPEED_APP_STORE_LINK, WATERSPEED_PLAY_STORE_LINK} from "constants/AppConstants.js";
import {isAndroid} from "FirebaseUtils";
import {mobileStoreLink} from "utils/mobileStore";
import {ws} from "styles/tokens";
import {
  AccentTextButton,
  CtaArrowIcon,
  GradientCtaButton,
  InnerPanel,
  PanelText,
  PanelTitle,
  PhotoCard
} from "./onboardingStyles";

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const BadgesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const StoreBadge = styled.img`
  height: 53px;
  width: 180px;
  object-fit: contain;
`;

const FooterGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 11px;
  width: 100%;
`;

const AccentLabel = styled(AccentTextButton).attrs({as: "p"})`
  margin: 0;
  padding: 0;
  border: none;
  font-size: 16px;
  color: ${ws.accentText};
  cursor: default;
`;

interface Props {
  onGoToWebapp: () => void;
}

/** Long enough for the screen to register before the store takes over. */
const STORE_REDIRECT_DELAY_MS = 1000;

function ReadySetGoStep({onGoToWebapp}: Props) {
  const {t} = useTranslation();

  // Phones go straight to their store; both badges stay for anyone who comes
  // back, blocks the redirect, or is on desktop.
  useEffect(() => {
    const storeLink = isAndroid ? null : mobileStoreLink();
    if (!storeLink) {
      return;
    }
    const timer = setTimeout(() => window.location.assign(storeLink), STORE_REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <PhotoCard>
      <InnerPanel gap="100px" style={{marginTop: 0, paddingTop: "42px"}}>
        <TitleGroup>
          <PanelTitle>{t("onboarding.readySetGo")}</PanelTitle>
          <PanelText>{t("onboarding.downloadTheApp")}</PanelText>
        </TitleGroup>
        <BadgesRow>
          <a href={WATERSPEED_APP_STORE_LINK} target="_blank" rel="noreferrer">
            <StoreBadge src="/assets/images/onboarding/badge-appstore.png" alt={t("onboarding.downloadTheApp")} />
          </a>
          <a href={WATERSPEED_PLAY_STORE_LINK} target="_blank" rel="noreferrer">
            <StoreBadge src="/assets/images/onboarding/badge-googleplay.png" alt={t("onboarding.downloadTheApp")} />
          </a>
        </BadgesRow>
        <FooterGroup>
          <AccentLabel>{t("onboarding.orContinueWebapp")}</AccentLabel>
          <GradientCtaButton small onClick={onGoToWebapp}>
            {t("onboarding.goToWebapp")}
            <CtaArrowIcon src="/assets/images/onboarding/arrow-cta.svg" alt="" />
          </GradientCtaButton>
        </FooterGroup>
      </InnerPanel>
    </PhotoCard>
  );
}

export default ReadySetGoStep;
